import { withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import React, { ComponentType, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { useUser } from '../../hooks/accounts';
import { useTheme } from '../../hooks/accounts';
import { useEnsembleModelsQuery, useDefaultEnsembleModels } from '../../hooks/tags';
import { useTenants } from '../../hooks/tenants';
import { ErrorPage, VerifyUser } from '../../pages/not-found/error-page';
import { Loading } from '../loading/loading';

interface Props {
  component: ComponentType;
}

const AuthRoute: React.FC<Props> = ({ component }) => {
  const [theme] = useTheme();
  const { user: authUser, loginWithRedirect } = useAuth0();
  const { pathname, search } = useLocation();

  // Store requested route before redirect (HOC handles authentication)
  useEffect(() => {
    // Only store route if user is not yet authenticated
    if (!authUser) {
      localStorage.setItem('requestedRoute', JSON.stringify({ pathname, search }));
      loginWithRedirect();
    }
  }, [authUser, loginWithRedirect, pathname, search]);

  // Memoize to prevent HOC from rendering a "new" component when something at the top of the tree changes.
  const Component = useMemo(
    () =>
      withAuthenticationRequired(component, {
        onRedirecting: () => <Loading />,
      }),
    [component],
  );

  // Critical bootstrap (blocking)
  const { tenants, isLoading: tenantsLoading, isError: tenantError } = useTenants();
  const { data: user, isLoading: userLoading, isError: userError } = useUser();

  // Non-critical bootstrap (background)
  useEnsembleModelsQuery({ suspense: false, staleTime: Infinity });
  useDefaultEnsembleModels({ suspense: false, staleTime: Infinity });

  // Email verification check - AFTER all hooks are called
  if (authUser && authUser.email_verified === false) {
    return (
      <ThemeProvider theme={theme}>
        <ErrorPage>
          <VerifyUser />
        </ErrorPage>
      </ThemeProvider>
    );
  }

  // Handle bootstrap errors
  if (tenantError || userError) throw new Error('Unable to bootstrap user / tenant data.');

  // Wait only for critical data
  if (tenantsLoading || userLoading || !tenants || !user) return <Loading />;

  return <Component />;
};

export default AuthRoute;
